import { ParticleEffect } from './ParticleEffect';
import type { Particle, IntensityColorScheme } from '../types';

/**
 * Spring petals effect
 */
export class SpringEffect extends ParticleEffect {
    protected getDefaultVariant(): string {
        return 'sakura';
    }

    protected getDefaultColorScheme(): IntensityColorScheme {
        return {
            minimal: '#ffe4e1',  // Misty rose
            subtle: '#ffc0cb',   // Pink
            moderate: '#ffb7d5', // Hot pink
            bold: '#ff69b4',     // Deep pink
        };
    }

    init(width: number, height: number): void {
        const count = this.getParticleCount();
        this.particles = [];

        for (let i = 0; i < count; i++) {
            this.particles.push(this.createPetal(width, height, true));
        }
    }

    private createPetal(width: number, height: number, randomY = false): Particle {
        const sizeMultiplier = this.getSizeMultiplier();
        const speedMultiplier = this.getSpeedMultiplier();
        const useIntensityColor = this.customization.intensityColors || this.customization.customColor;

        const colors = {
            sakura: ['#ffb7d5', '#ffc0cb', '#ffe4e1'],
            softPetals: ['#f8e1e6', '#f0d5d8', '#ffe4e1'],
            pollen: ['#fffacd', '#ffffe0', '#fafad2'],
        };

        const colorSet = colors[this.variant as keyof typeof colors] || colors.sakura;
        const particleColor = useIntensityColor
            ? this.getIntensityColor()
            : colorSet[Math.floor(Math.random() * colorSet.length)];

        const particle: Particle = {
            x: Math.random() * width,
            y: randomY ? Math.random() * height : -10,
            vx: (Math.random() - 0.5) * 1 * speedMultiplier,
            vy: (0.3 + Math.random() * 0.8) * speedMultiplier,
            size: (3 + Math.random() * 4) * sizeMultiplier,
            opacity: 0.4 + Math.random() * 0.4,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.05,
            color: particleColor,
        };

        // Try to add custom image if available
        if (this.hasCustomImages()) {
            particle.image = this.getRandomImage();
        }

        return particle;
    }

    update(_deltaTime: number, width: number, height: number): void {
        // Ensure particles have images if they loaded after particle creation
        this.ensureParticleImages();

        for (let i = 0; i < this.particles.length; i++) {
            const p = this.particles[i];

            // Swirling motion
            const time = Date.now() / 1000;
            p.x += p.vx + Math.sin(time * 2 + i) * 0.5;
            p.y += p.vy;
            p.rotation += p.rotationSpeed;

            // Reset if off screen
            if (p.y > height + 10) {
                this.particles[i] = this.createPetal(width, height, false);
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const p of this.particles) {
            // Use custom image if available
            if (p.image && p.image.complete && p.image.naturalHeight > 0) {
                this.renderParticle(ctx, p);
            } else {
                // Default petal rendering
                ctx.save();
                ctx.globalAlpha = p.opacity;
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);

                ctx.fillStyle = p.color!;
                ctx.beginPath();
                ctx.ellipse(0, 0, p.size, p.size * 0.6, 0, 0, Math.PI * 2);
                ctx.fill();

                ctx.restore();
            }
        }
    }
}
